// Step 1 hero graphic for the EvaluationProcess section — a continuously
// scrolling "equity climbing" chart with a tracking dot, target line and
// a floating PnL chip. Pulled out of EvaluationProcess.jsx so that
// file no longer carries ~200 lines of canvas math; both `ClimbingChart`
// and the shared `CheckerBg` background utility live here together.
//
// All animation runs inside `useAnimationFrame` (framer-motion) so it
// participates in the same RAF loop as the rest of the page — no
// independent setInterval / requestAnimationFrame leaks on unmount.

import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimationFrame } from "framer-motion";
import { TrendingUp } from "lucide-react";

/* Shared checkered background used by every Step graphic. Exported
   because StepCard for Steps 2/3/4 also pull this in to keep their
   chrome consistent. */
export function CheckerBg({ density = 24 }) {
  return (
    <>
      <div
        className="absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage:
            "linear-gradient(45deg, transparent 49%, color-mix(in srgb, var(--brand) 55%, transparent) 49% 51%, transparent 51%), linear-gradient(-45deg, transparent 49%, color-mix(in srgb, var(--brand) 35%, transparent) 49% 51%, transparent 51%)",
          backgroundSize: `${density}px ${density}px`,
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(color-mix(in srgb, var(--brand) 70%, transparent) 1px, transparent 1px)",
          backgroundSize: "12px 12px",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 0%, color-mix(in srgb, #000 70%, transparent) 95%)",
        }}
      />
    </>
  );
}

/* Continuously-scrolling climbing chart for Step 1. Tracks the right-
   edge of the visible window with a glowing dot + floating PnL pill,
   and ticks the % counter every 0.9s for a "live equity" feel. */
export function ClimbingChart() {
  const xRef = useRef(0);
  const pathGroupRef = useRef(null);
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const labelRef = useRef(null);
  const lineH = 180;
  const lineW = 360;
  const DOT_INSET_VX = 22;
  const DOT_X_PCT = ((lineW - DOT_INSET_VX) / lineW) * 100;

  // Pre-compute a long upward-trending wavy path. Memoised so the
  // expensive 480-point math only runs once per mount.
  const { d, viewW, pointFn } = useMemo(() => {
    const points = 480;
    const w = 3600;
    const startY = 175;
    const endY = 8;
    let s = `M 0 ${startY}`;
    const yAt = (i) => {
      const base = startY + (endY - startY) * (i / points);
      return base + Math.sin(i * 0.35) * 4 + Math.cos(i * 0.18) * 6 + Math.sin(i * 0.07) * 9;
    };
    for (let i = 1; i <= points; i++) {
      const x = (i / points) * w;
      s += ` L ${x.toFixed(1)} ${yAt(i).toFixed(1)}`;
    }
    const pointFn = (vx) => {
      const i = (vx / w) * points;
      const i0 = Math.floor(i);
      const i1 = Math.min(points, i0 + 1);
      const t = i - i0;
      return yAt(i0) * (1 - t) + yAt(i1) * t;
    };
    return { d: s, viewW: w, pointFn };
  }, []);

  // Path translation + dot/label position updated each frame. Dot and
  // label are HTML positioned by PERCENT of the container so the SVG
  // can stretch freely without distorting the circle into an ellipse.
  useAnimationFrame((_, delta) => {
    const speed = 26;
    xRef.current = (xRef.current + (speed * delta) / 1000) % (viewW - lineW);
    if (pathGroupRef.current) {
      pathGroupRef.current.setAttribute("transform", `translate(${-xRef.current}, 0)`);
    }
    const visibleEdgeVx = xRef.current + lineW - DOT_INSET_VX;
    const dotY = pointFn(visibleEdgeVx);
    const dotPct = (dotY / lineH) * 100;
    if (dotRef.current) dotRef.current.style.top = `${dotPct}%`;
    if (ringRef.current) ringRef.current.style.top = `${dotPct}%`;
    if (labelRef.current) labelRef.current.style.top = `calc(${dotPct}% - 22px)`;
  });

  const [pctText, setPctText] = useState("+8.42%");
  useEffect(() => {
    let v = 8.42;
    const id = setInterval(() => {
      v += 0.01 + Math.random() * 0.03;
      setPctText(`+${v.toFixed(2)}%`);
    }, 900);
    return () => clearInterval(id);
  }, []);

  const TARGET_PCT = (34 / lineH) * 100;

  return (
    <div
      className="relative aspect-[16/9] rounded-xl overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(170,222,235,0.06), rgba(0,0,0,0.4))",
        border: "1px solid color-mix(in srgb, var(--brand) 14%, transparent)",
      }}
    >
      <CheckerBg density={20} />

      {/* Path-only SVG. Lines are stretched to fit; text and dots are
          HTML overlays so they never warp regardless of aspect ratio. */}
      <svg viewBox={`0 0 ${lineW} ${lineH}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id="climbArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#AADEEB" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#AADEEB" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="climbLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#5fb8cf" />
            <stop offset="100%" stopColor="#AADEEB" />
          </linearGradient>
          <clipPath id="chartClip"><rect x="0" y="0" width={lineW} height={lineH} /></clipPath>
        </defs>

        {[40, 80, 120, 160].map((y) => (
          <line key={y} x1="0" y1={y} x2={lineW} y2={y} stroke="#AADEEB" strokeOpacity="0.06" />
        ))}
        <line x1="0" y1="34" x2={lineW} y2="34" stroke="#AADEEB" strokeOpacity="0.5" strokeDasharray="4 4" />

        <g clipPath="url(#chartClip)">
          <g ref={pathGroupRef}>
            <path d={`${d} L ${viewW} ${lineH} L 0 ${lineH} Z`} fill="url(#climbArea)" />
            <path d={d} fill="none" stroke="url(#climbLine)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </g>
        </g>
      </svg>

      {/* BULL pill — top-LEFT */}
      <div
        className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.18em] z-10"
        style={{ background: "rgba(80,220,130,0.10)", border: "1px solid rgba(80,220,130,0.32)", color: "#54d97c" }}
      >
        <span className="w-1 h-1 rounded-full bg-[#54d97c] animate-pulse" style={{ boxShadow: "0 0 6px #54d97c" }} />
        BULL
      </div>

      {/* EQUITY GROWTH TARGET — top-RIGHT, aligned to the dashed line.
          Hidden on the smallest viewport so it never collides with the
          BULL pill on the left. */}
      <div
        className="hidden sm:block absolute right-2 z-10 px-1.5 py-[1px] rounded text-[10px] font-mono uppercase tracking-[0.20em] pointer-events-none whitespace-nowrap"
        style={{
          top: `calc(${TARGET_PCT}% - 16px)`,
          color: "#AADEEB",
          background: "rgba(0,0,0,0.45)",
          border: "1px solid color-mix(in srgb, var(--brand) 28%, transparent)",
        }}
      >
        EQUITY · GROWTH · TARGET
      </div>

      {/* Tick rail — bottom-left bar visualiser */}
      <div className="absolute bottom-2 left-2 flex items-end gap-1 z-10" style={{ height: 12 }}>
        {[...Array(6)].map((_, i) => (
          <motion.span
            key={`tick-${i}`}
            className="block w-0.5 rounded-full"
            style={{
              background: i % 5 === 4 ? "#f06a6a" : "#54d97c",
              boxShadow: `0 0 4px ${i % 5 === 4 ? "#f06a6a" : "#54d97c"}`,
            }}
            animate={{ height: [4, 10 + i * 1.2, 4], opacity: [0.4, 0.95, 0.4] }}
            transition={{ duration: 1.4 + i * 0.18, repeat: Infinity, ease: "easeInOut", delay: i * 0.12 }}
          />
        ))}
      </div>

      {/* Pulsing ring — HTML, perfectly circular regardless of stretch */}
      <span
        ref={ringRef}
        aria-hidden
        className="absolute z-10 rounded-full pointer-events-none"
        style={{
          left: `${DOT_X_PCT}%`,
          top: "12%",
          width: 22,
          height: 22,
          transform: "translate(-50%, -50%)",
          border: "1.4px solid #54d97c",
          opacity: 0.55,
          animation: "climb-pulse 1.6s ease-in-out infinite",
        }}
      />
      {/* Solid dot — HTML, perfectly circular */}
      <span
        ref={dotRef}
        aria-hidden
        className="absolute z-10 rounded-full pointer-events-none"
        style={{
          left: `${DOT_X_PCT}%`,
          top: "12%",
          width: 9,
          height: 9,
          transform: "translate(-50%, -50%)",
          background: "#54d97c",
          boxShadow: "0 0 8px #54d97c",
        }}
      />

      {/* Floating PnL chip — follows dot Y */}
      <div
        ref={labelRef}
        className="absolute z-10 px-2 py-[2px] rounded-md text-[12px] font-medium tracking-wider whitespace-nowrap"
        style={{
          right: 12,
          top: "12%",
          transform: "translateY(-50%)",
          background: "rgba(80,220,130,0.16)",
          border: "1px solid rgba(80,220,130,0.45)",
          color: "#54d97c",
        }}
      >
        {pctText} <TrendingUp size={10} className="inline ml-0.5 -mt-0.5" />
      </div>
    </div>
  );
}
